import type {
  CourseCashFeeCarve,
  CourseCashReceipt,
  CourseCashTransaction,
} from 'tio-common/types/course-cash-transaction';
import type { TasAssistanceExtension as TasAssistanceModel } from 'tio-data/data/resources/tas-assistance.ext';
import type { TasApprovableExtension as ApprovableModel } from 'tio-data/data/resources/tas-approvable.ext';

/**
 * Transaction Adapter
 *
 * Transforms backend TasAssistance models into the UI's CourseCashTransaction shape.
 * This adapter provides a single point to map from TasAssistance records with
 * CAPTURED state to the common transaction interface.
 */

/**
 * Map TasAssistance records (Course Cash with CAPTURED state) to UI transaction shape.
 *
 * @param assistances - Array of TasAssistance models from the application
 * @param approvablesByAssistance - Map of assistance ID → approvable models,
 *   queried directly from the /tas-approvables endpoint. When provided, takes
 *   precedence over the sideloaded relationship data on each assistance.
 * @param options - `includeAuthorized` additionally maps AUTHORIZED (swiped,
 *   not yet captured) assistances as informational `pendingCapture`
 *   transactions. Off by default so the admin/approval surfaces sharing this
 *   adapter are untouched.
 * @returns Array of CourseCashTransaction for UI consumption
 */
export function mapAssistancesToTransactions(
  assistances: TasAssistanceModel[],
  approvablesByAssistance?: Map<string, ApprovableModel[]>,
  options: { includeAuthorized?: boolean } = {}
): CourseCashTransaction[] {
  const captured = assistances.filter((a): a is TasAssistanceModel & { id: string } =>
    Boolean(a.isCourseCash && a.isCaptured && a.id)
  );

  // Group the legs of a clean 2-auth split. Both legs carry the SAME lcu_hint
  // fee/charge sub-objects, so we key off those shared values rather than matching
  // the digest's assistance_id against the model id: the digest ids are plain JSON
  // numbers that lose precision above 2^53, so they can never equal the exact string
  // model id. The key below is identical for both legs of one swipe and distinct
  // across swipes (the imprecise ids round identically on both legs).
  const splitGroups = new Map<string, (TasAssistanceModel & { id: string })[]>();
  for (const a of captured) {
    const key = splitGroupKey(a);
    if (key) {
      const list = splitGroups.get(key) ?? [];
      list.push(a);
      splitGroups.set(key, list);
    }
  }

  const handled = new Set<string>();
  const transactions: CourseCashTransaction[] = [];

  for (const assistance of captured) {
    if (handled.has(assistance.id)) continue;

    const key = splitGroupKey(assistance);
    if (key) {
      const group = splitGroups.get(key) ?? [];
      const chargeLeg = group.find((a) => a.digests?.lcu_hint?.role === 'charge');
      const feeLeg = group.find((a) => a.digests?.lcu_hint?.role === 'fee');
      // Merge only a well-formed pair: exactly one charge leg + one fee leg. Anything
      // else (a partner missing from the captured set, or an ambiguous group) falls
      // back to a single transaction with the split neutralized so it can't mis-split.
      if (group.length === 2 && chargeLeg && feeLeg && chargeLeg.id !== feeLeg.id) {
        handled.add(chargeLeg.id);
        handled.add(feeLeg.id);
        transactions.push(buildMergedTransaction(chargeLeg, feeLeg, approvablesByAssistance));
        continue;
      }
      handled.add(assistance.id);
      transactions.push(buildSingleTransaction(assistance, approvablesByAssistance, true));
      continue;
    }

    handled.add(assistance.id);
    transactions.push(buildSingleTransaction(assistance, approvablesByAssistance));
  }

  if (options.includeAuthorized) {
    const authorized = assistances.filter((a): a is TasAssistanceModel & { id: string } =>
      Boolean(a.isCourseCash && a.isAuthorized && a.id)
    );
    for (const assistance of authorized) {
      transactions.push(buildAuthorizedTransaction(assistance));
    }
  }

  return transactions;
}

/**
 * An AUTHORIZED swipe awaiting capture, surfaced (behind `includeAuthorized`)
 * so it appears on the employee manage screen instead of nowhere until capture.
 * `receiptStatus: 'not_required'` keeps it out of the receipt-task pipeline and
 * the admin awaiting-review counts. No split-merge here: capture is what settles
 * a 2-auth split's real legs, so until then each authorized leg is a plain
 * single transaction.
 */
function buildAuthorizedTransaction(
  assistance: TasAssistanceModel & { id: string }
): CourseCashTransaction {
  return {
    id: assistance.id,
    pendingCapture: true,
    transactionDate: assistance.authorizedDate?.toISOString().split('T')[0] ?? '',
    merchantName: extractMerchantName(assistance),
    amount: Math.abs(assistance.amount ?? 0),
    receiptStatus: 'not_required',
    receipts: [],
    description: extractDescription(assistance),
    category: 'Education',
    assistanceId: assistance.id,
    lineItems: [],
  };
}

/**
 * A key identifying the swipe a clean 2-auth split leg belongs to, or null when the
 * assistance is not a clean split leg. Both legs of one swipe produce the same key.
 */
function splitGroupKey(assistance: TasAssistanceModel): string | null {
  const lcu = assistance.digests?.lcu_hint;
  if (
    !lcu ||
    !lcu.fee ||
    !lcu.charge ||
    lcu.fee.assistance_id == null ||
    lcu.charge.assistance_id == null ||
    String(lcu.fee.assistance_id) === String(lcu.charge.assistance_id) ||
    (lcu.role !== 'fee' && lcu.role !== 'charge')
  ) {
    return null;
  }
  // Include the authorization ids alongside the assistance ids: all four are lossy
  // (rounded) JSON numbers, but requiring every one of them AND the gross to collide
  // makes two different swipes sharing a bucket practically impossible.
  return [
    lcu.charge.assistance_id,
    lcu.fee.assistance_id,
    lcu.charge.authorization_id,
    lcu.fee.authorization_id,
    lcu.gross_amount,
  ].join(':');
}

/**
 * Returns the sideloaded fee carve, or undefined for missing or soft-deleted rows; only durable
 * rows establish that a fee was carved.
 */
function extractFeeCarve(assistance: TasAssistanceModel): CourseCashFeeCarve | undefined {
  const row = assistance.pcsFeeDisambiguation;
  if (!row?.id || row.deletedAt) return undefined;

  return {
    id: row.id,
    state: row.state,
    source: row.source,
    coverState: row.coverState,
    grossAmount: row.grossAmount,
    feeAmount: row.feeAmount,
    netAmount: row.netAmount,
    feeRate: row.feeRate,
    reversedAt: row.reversedAt,
  };
}

/** Resolve approvables for an assistance: endpoint-queried map first, then sideloaded relationship. */
function resolveApprovables(
  assistance: TasAssistanceModel & { id: string },
  approvablesByAssistance?: Map<string, ApprovableModel[]>
): ApprovableModel[] {
  return approvablesByAssistance?.get(assistance.id) ?? assistance.courseCashApprovables ?? [];
}

/** Build receipts from approvables, falling back to legacy attachment-based logic. */
function buildReceipts(
  assistance: TasAssistanceModel,
  approvables: ApprovableModel[]
): CourseCashReceipt[] {
  if (approvables.length > 0) {
    return mapReceiptsFromApprovables(assistance, approvables);
  }
  console.warn(
    `[CourseCash Fallback] Assistance ${assistance.id} has no TasApprovable records. ` +
      `Using legacy attachment-based logic. This assistance needs migration.`
  );
  return mapReceiptsFromAttachments(assistance);
}

/** Map a single (non-split) assistance to a transaction. */
function buildSingleTransaction(
  assistance: TasAssistanceModel & { id: string },
  approvablesByAssistance?: Map<string, ApprovableModel[]>,
  dropLcuSplit = false
): CourseCashTransaction {
  const approvables = resolveApprovables(assistance, approvablesByAssistance);
  const receipts = buildReceipts(assistance, approvables);
  return {
    id: assistance.id,
    transactionDate: assistance.capturedDate?.toISOString().split('T')[0] ?? '',
    merchantName: extractMerchantName(assistance),
    amount: Math.abs(assistance.amount ?? 0),
    grossAmount: assistance.grossAmount != null ? Math.abs(assistance.grossAmount) : undefined,
    receiptStatus: determineReceiptStatus(receipts, approvables),
    receiptDueDate: assistance.receiptDueDate?.toISOString().split('T')[0],
    receipts,
    description: extractDescription(assistance),
    category: 'Education',
    assistanceId: assistance.id,
    // A split leg whose partner is absent: drop only the lcu_hint so the whole-swipe
    // split is not applied to this single leg's (smaller) captured amount — but keep
    // evidence_hint so the receipt-evidence panel can still inform the reviewer.
    digests: dropLcuSplit
      ? assistance.digests?.evidence_hint
        ? { evidence_hint: assistance.digests.evidence_hint }
        : undefined
      : (assistance.digests ?? undefined),
    feeCarve: extractFeeCarve(assistance),
    lineItems: [],
  };
}

/**
 * Merge a clean 2-auth split (charge leg + fee leg) into one transaction representing
 * the whole swipe. Follows the single-assistance convention: `amount` is the tuition
 * portion (the charge leg's captured amount), `grossAmount` is the full swipe (charge
 * + fee) — so the drawer row shows the charge and the reviewed Amount/Fee readouts
 * (fee = gross − amount) resolve to the actual legs. Each leg is carried in
 * `splitLegs` so the approval flow can affirm both approvables and write each leg
 * back by its role.
 */
function buildMergedTransaction(
  chargeLeg: TasAssistanceModel & { id: string },
  feeLeg: TasAssistanceModel & { id: string },
  approvablesByAssistance?: Map<string, ApprovableModel[]>
): CourseCashTransaction {
  // Source each leg's ORIGINAL captured value (effectiveAmount = gross || amount),
  // not its current ledger amount: once the backend carve runs, the fee leg's
  // amount is 0 with the fee moved to gross — reading amount would display a $0
  // fee and a merged total missing the fee, contradicting the lcu_hint row.
  const chargeCaptured = Math.abs(chargeLeg.effectiveAmount ?? chargeLeg.amount ?? 0);
  const feeCaptured = Math.abs(feeLeg.effectiveAmount ?? feeLeg.amount ?? 0);
  const gross = chargeCaptured + feeCaptured;
  const chargeApprovables = resolveApprovables(chargeLeg, approvablesByAssistance);
  const feeApprovables = resolveApprovables(feeLeg, approvablesByAssistance);
  const approvables = [...chargeApprovables, ...feeApprovables];
  const receipts = dedupeReceipts([
    ...buildReceipts(chargeLeg, chargeApprovables),
    ...buildReceipts(feeLeg, feeApprovables),
  ]);
  // evidence_hint is one entry per approvable, and a split's receipt approvable can
  // live on either leg — union both legs' entries so the recommendation panel can
  // resolve the selected approvable regardless of which leg carries the receipt.
  const evidenceHint = [
    ...(chargeLeg.digests?.evidence_hint ?? []),
    ...(feeLeg.digests?.evidence_hint ?? []),
  ];
  const digests =
    chargeLeg.digests || evidenceHint.length
      ? {
          ...(chargeLeg.digests ?? {}),
          ...(evidenceHint.length ? { evidence_hint: evidenceHint } : {}),
        }
      : undefined;
  return {
    id: chargeLeg.id,
    transactionDate: chargeLeg.capturedDate?.toISOString().split('T')[0] ?? '',
    merchantName: extractMerchantName(chargeLeg),
    amount: chargeCaptured, // tuition portion (charge leg); fee derives as gross − amount
    grossAmount: gross,
    receiptStatus: determineReceiptStatus(receipts, approvables),
    receiptDueDate: chargeLeg.receiptDueDate?.toISOString().split('T')[0],
    receipts,
    description: extractDescription(chargeLeg),
    category: 'Education',
    assistanceId: chargeLeg.id,
    digests,
    splitLegs: [
      { assistanceId: chargeLeg.id, role: 'charge', capturedAmount: chargeCaptured },
      { assistanceId: feeLeg.id, role: 'fee', capturedAmount: feeCaptured },
    ],
    // The carve zeroes the fee-bearing leg, so on a clean 2-auth split the row belongs to
    // the fee leg; fall back to the charge leg for the (unexpected) inverse.
    feeCarve: extractFeeCarve(feeLeg) ?? extractFeeCarve(chargeLeg),
    lineItems: [],
  };
}

/**
 * Transactions with a receipt awaiting admin review ('uploaded' or 'changes_needed').
 * The admin verification banner counts these — deriving from transaction-level
 * receiptStatus (any APPROVED approvable ⇒ verified, splits merged to one) rather
 * than raw approvable rows, so a stale orphaned PENDING can't block a cleared app
 * and a split swipe can't double-count.
 */
export function countTransactionsAwaitingReview(transactions: CourseCashTransaction[]): number {
  return transactions.filter(
    (t) => t.receiptStatus === 'uploaded' || t.receiptStatus === 'changes_needed'
  ).length;
}

/** Dedupe receipts that point at the same asset/approvable (split legs often share a proof). */
function dedupeReceipts(receipts: CourseCashReceipt[]): CourseCashReceipt[] {
  const seen = new Set<string>();
  return receipts.filter((r) => {
    const key = r.assetId ?? r.approvableId ?? r.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Determine receipt status from approvable state (primary) or receipt presence (fallback).
 *
 * Priority: approvable state > receipt count > 'needed'
 */
export function determineReceiptStatus(
  receipts: CourseCashReceipt[],
  approvables: ApprovableModel[]
): CourseCashTransaction['receiptStatus'] {
  if (receipts.length === 0 && approvables.length === 0) {
    return 'needed';
  }

  // When approvables exist, derive status from their state
  const courseCashApprovables = approvables.filter((a) => a.isCourseCashUsageReview);

  if (courseCashApprovables.length === 0) {
    // Receipts exist but approvable hasn't been created yet (Sidekiq job lag)
    return receipts.length > 0 ? 'uploaded' : 'needed';
  }

  // If ANY approvable is approved/affirmed, the transaction is verified
  if (courseCashApprovables.some((a) => a.isApproved)) return 'verified';

  // If ANY is in PREPARING state (changes needed), allow re-upload
  if (courseCashApprovables.some((a) => a.isPreparing)) return 'changes_needed';

  // If all are abandoned, the user removed previous receipts — still needs a new one
  const allAbandoned = courseCashApprovables.every((a) => a.isAbandoned);
  if (allAbandoned) return 'needed';

  // If a non-abandoned, non-rejected approvable exists (e.g. new upload after rejection),
  // the transaction is in review — don't let an old rejected approvable override it.
  const hasActivePending = courseCashApprovables.some(
    (a) => !a.isAbandoned && !a.isRejected && !a.isApproved
  );
  if (hasActivePending) return 'uploaded';

  // Only show rejected when ALL non-abandoned approvables are rejected (no new upload yet)
  if (courseCashApprovables.some((a) => a.isRejected)) return 'rejected';

  // Default: in review (PENDING)
  return 'uploaded';
}

/**
 * Map approvable records to CourseCashReceipt shape.
 * Each approvable links to a TasAsset (the receipt file) via its polymorphic `approvable` relationship.
 *
 * NOTE: We use belongsTo('approvable').id() + assistance.tasAssets lookup instead of
 * traversing approvable.approvable directly, because the backend returns the relationship
 * linkage with type "digital-assets" (base STI type) while the included record serializes
 * as "tas-assets" (subtype). This type mismatch prevents Ember Data from resolving the
 * sync polymorphic relationship.
 *
 * @param assistance - The parent assistance, used to look up loaded TasAsset records
 * @param approvables - Approvable models for this assistance, from endpoint or relationship
 */
export function mapReceiptsFromApprovables(
  assistance: TasAssistanceModel,
  approvables: ApprovableModel[]
): CourseCashReceipt[] {
  return approvables
    .filter((approvable) => !approvable.isAbandoned)
    .filter((approvable) => {
      // A robot-cleared approvable is keyed to the PcsLogicalCardUsage itself, not a
      // receipt asset. Mapping it as a receipt fabricates a "Receipt uploaded" entry
      // (assetId = LCU id) with a dead view link and a Remove that throws. Match the
      // linkage type loosely so any inflection (pcsLogicalCardUsages /
      // pcs-logical-card-usages) is excluded without risking real digital-asset receipts.
      // belongsTo()'s parameter is typed `never` (polymorphic empty-string type
      // declaration), so narrow via cast to read the linkage identifier.
      const ref = (
        approvable as unknown as {
          belongsTo(key: string): { identifier: { type: string } | null };
        }
      ).belongsTo('approvable');
      const linkageType = ref.identifier?.type ?? '';
      return !/logical.?card.?usage/i.test(linkageType);
    })
    .map((approvable) => {
      // Get the TasAsset ID from the raw relationship data without traversing the
      // polymorphic relationship (which fails due to digital-assets vs tas-assets STI mismatch)
      // @ts-expect-error - belongsTo() typed as 'never' due to polymorphic empty-string type declaration
      const assetId = approvable.belongsTo('approvable').id() as string | null;
      const tasAsset = assistance.tasAssets?.find((a) => a.id === assetId);
      const attachments = tasAsset?.attachments ?? [];
      const attachment = attachments[0];

      return {
        id: approvable.id ?? `approvable-${Date.now()}`,
        attachmentId: attachment?.id ?? undefined,
        assetId: assetId ?? undefined,
        approvableId: approvable.id ?? undefined,
        approvableState: approvable.state,
        filename: attachment?.filename ?? attachment?.name ?? 'Receipt',
        contentType: attachment?.contentType,
        uploadedAt: approvable.createdAt ?? new Date().toISOString(),
        receiptUrl: attachment?.signedUrl ?? '',
        status: mapApprovableStateToReceiptStatus(approvable.state),
      };
    });
}

/**
 * Map an approvable AASM state to the CourseCashReceipt status.
 */
export function mapApprovableStateToReceiptStatus(state: string): CourseCashReceipt['status'] {
  switch (state) {
    case 'ApprovableState.APPROVED':
      return 'verified';
    case 'ApprovableState.REJECTED':
      return 'rejected';
    case 'ApprovableState.PREPARING':
      return 'changes_needed';
    // AFFIRMED/NOT_AFFIRMED are transitory states that auto-cascade;
    // they should never appear at rest, but default handles them.
    default:
      return 'pending_verification';
  }
}

/**
 * Legacy: Map receipts from CARD_USAGE_PROOF attachments.
 * Used as fallback for pre-migration assistances without TasApprovable records.
 */
export function mapReceiptsFromAttachments(assistance: TasAssistanceModel): CourseCashReceipt[] {
  const attachments = assistance.attachments ?? [];
  const cardUsageProofAttachments = attachments.filter((attachment) => {
    const isCardUsageProof = attachment.displayClassification === 'CARD_USAGE_PROOF';
    const isDeleted = Boolean(attachment.attachmentDeletedAt);
    return isCardUsageProof && !isDeleted;
  });

  const receipts: CourseCashReceipt[] = [];

  for (const attachment of cardUsageProofAttachments) {
    if (!attachment.id) {
      console.warn('Skipping receipt attachment without id', { attachment });
      continue;
    }

    const filename = attachment.filename || attachment.name || 'Receipt';
    const uploadedAt = attachment.createdAt || new Date().toISOString();

    receipts.push({
      id: attachment.id,
      attachmentId: attachment.id,
      assetId: attachment.digitalAsset?.id ?? undefined,
      filename,
      contentType: attachment.contentType,
      uploadedAt,
      receiptUrl: attachment.signedUrl || attachment.digitalAsset?.signedUrl || '',
      status: 'pending_verification',
    });
  }

  return receipts;
}

/**
 * Last-resort label when neither the card network nor the program instance names anyone.
 * Exported so callers that would rather render nothing than a placeholder (the
 * "Needs Your Attention" receipt row) can tell "unknown" from a real name.
 */
export const PLACEHOLDER_MERCHANT_NAME = 'Merchant';

/**
 * Where the swipe actually landed: the card-network merchant the server resolves through
 * this assistance's authorization. Falls back to the program instance's
 * SCHOOL_INSTITUTION_NAME — the school the employee is enrolled at, which is usually but
 * not always who the card was charged to — and finally to a generic placeholder.
 */
export function extractMerchantName(assistance: TasAssistanceModel): string {
  const merchantName = assistance.merchantName;
  if (merchantName) return merchantName;

  const institutionName = assistance.tasApplication?.tasProgramInstance?.institutionName;
  if (institutionName) return institutionName;

  return PLACEHOLDER_MERCHANT_NAME;
}

/**
 * Extract transaction description
 */
export function extractDescription(assistance: TasAssistanceModel): string | undefined {
  return assistance.fields?.description as string | undefined;
}

/**
 * Transforms PcsAuthorization model to CourseCashTransaction
 *
 * @deprecated Use mapAssistancesToTransactions instead - this is a placeholder
 * @param authorization - The PcsAuthorization model from Ember Data
 * @returns CourseCashTransaction - Transformed transaction data
 */
export function fromPcsAuthorization(authorization: unknown): CourseCashTransaction {
  // TODO: Replace with actual model mapping when PcsAuthorization is available
  // This is a placeholder structure to show expected transformation
  const auth = authorization as Record<string, unknown>;
  const receiptUrl = auth.receiptUrl as string | undefined;
  const receipts: CourseCashReceipt[] = receiptUrl
    ? [
        {
          id: `legacy-${String(auth.id ?? '')}`,
          attachmentId: undefined,
          assetId: undefined,
          filename: 'Receipt',
          uploadedAt: new Date().toISOString(),
          receiptUrl,
          status: 'pending_verification',
        },
      ]
    : [];

  return {
    id: (auth.id as string) || '',
    transactionDate: (auth.createdAt as string) || new Date().toISOString(),
    merchantName: (auth.merchantName as string) || 'Merchant',
    amount: parseFloat((auth.amount as string) || '0'),
    receiptStatus: mapReceiptStatus(auth.receiptStatus as string | undefined),
    receiptDueDate: auth.receiptDueDate as string | undefined,
    receipts,
    description: auth.description as string | undefined,
    category: auth.category as string | undefined,
    cardId: auth.cardId as string | undefined,
    lineItems: [],
  };
}

/**
 * Maps backend receipt status to UI receipt status
 */
function mapReceiptStatus(
  backendStatus: string | undefined
): CourseCashTransaction['receiptStatus'] {
  switch (backendStatus?.toLowerCase()) {
    case 'needed':
    case 'pending':
      return 'needed';
    case 'uploaded':
    case 'submitted':
      return 'uploaded';
    case 'verified':
    case 'approved':
      return 'verified';
    case 'not_required':
    case 'not-required':
    case 'na':
      return 'not_required';
    default:
      return 'needed'; // Default to needed if status is unclear
  }
}

/**
 * Filters transactions that need receipt upload
 */
export function getUnverifiedTransactions(
  transactions: CourseCashTransaction[]
): CourseCashTransaction[] {
  return transactions.filter((t) => t.receiptStatus === 'needed');
}

/**
 * Groups transactions by receipt due date
 */
export function groupByDueDate(
  transactions: CourseCashTransaction[]
): Map<string, CourseCashTransaction[]> {
  const grouped = new Map<string, CourseCashTransaction[]>();

  for (const transaction of transactions) {
    const dueDate = transaction.receiptDueDate || 'No due date';
    const existing = grouped.get(dueDate) || [];
    existing.push(transaction);
    grouped.set(dueDate, existing);
  }

  return grouped;
}

/**
 * Sorts transactions by date (newest first)
 */
export function sortByDate(transactions: CourseCashTransaction[]): CourseCashTransaction[] {
  return [...transactions].sort((a, b) => {
    const dateA = new Date(a.transactionDate).getTime();
    const dateB = new Date(b.transactionDate).getTime();
    return dateB - dateA; // Newest first
  });
}
